import type { IeltsModule, IeltsTaskType } from "./criteria";

type ChartKind = "bar" | "line" | "pie" | "table";
export interface ChartPoint {
  label: string;
  value: number;
}
interface ChartSeries {
  name: string;
  points: ChartPoint[];
}
export interface ChartSpec {
  kind: ChartKind;
  title: string;
  unit: string; // e.g. "%", "millions", "°C"
  categoryAxis: string; // x-axis / category label
  series: ChartSeries[]; // one series for bar/pie/table-with-one-col; multiple for line/grouped-bar
}

export interface Stimulus {
  id: string;
  module: IeltsModule;
  taskType: IeltsTaskType;
  promptText: string; // the human task instruction shown to the learner (no raw numbers)
  chartSpec?: ChartSpec; // Academic Task 1
  bulletPoints?: string[]; // General Training Task 1 (the three required points)
}

const TASK1_ACADEMIC_INSTRUCTION =
  "The chart below shows the data described in the title. Summarise the information by selecting and reporting the main features, and make comparisons where relevant. Write at least 150 words.";

const TASK1_GENERAL_INSTRUCTION_PREFIX =
  "You should write at least 150 words. Write a letter. In your letter:";

const STIMULI: Stimulus[] = [
  // ---- Academic Task 1 — charts ----
  {
    id: "t1-a-coffee",
    module: "academic",
    taskType: "task1",
    promptText: TASK1_ACADEMIC_INSTRUCTION,
    chartSpec: {
      kind: "bar",
      title: "Average weekly cups of coffee consumed per person, by age group (2023)",
      unit: "cups",
      categoryAxis: "Age group",
      series: [
        {
          name: "Cups per week",
          points: [
            { label: "18–29", value: 9 },
            { label: "30–44", value: 14 },
            { label: "45–59", value: 12 },
            { label: "60+", value: 7 },
          ],
        },
      ],
    },
  },
  {
    id: "t1-a-internet",
    module: "academic",
    taskType: "task1",
    promptText: TASK1_ACADEMIC_INSTRUCTION,
    chartSpec: {
      kind: "line",
      title: "Households with home internet access in two countries, 2000–2020 (%)",
      unit: "%",
      categoryAxis: "Year",
      series: [
        {
          name: "Country A",
          points: [
            { label: "2000", value: 20 },
            { label: "2005", value: 45 },
            { label: "2010", value: 70 },
            { label: "2015", value: 85 },
            { label: "2020", value: 93 },
          ],
        },
        {
          name: "Country B",
          points: [
            { label: "2000", value: 8 },
            { label: "2005", value: 22 },
            { label: "2010", value: 48 },
            { label: "2015", value: 72 },
            { label: "2020", value: 88 },
          ],
        },
      ],
    },
  },
  {
    id: "t1-a-transport",
    module: "academic",
    taskType: "task1",
    promptText: TASK1_ACADEMIC_INSTRUCTION,
    chartSpec: {
      kind: "pie",
      title: "How commuters in one city travelled to work (2022)",
      unit: "%",
      categoryAxis: "Mode",
      series: [
        {
          name: "Share",
          points: [
            { label: "Car", value: 42 },
            { label: "Bus", value: 24 },
            { label: "Train", value: 18 },
            { label: "Cycling", value: 9 },
            { label: "Walking", value: 7 },
          ],
        },
      ],
    },
  },
  // ---- General Training Task 1 — letters ----
  {
    id: "t1-g-neighbour",
    module: "general",
    taskType: "task1",
    promptText:
      "A new family has just moved into the house next door to you. You would like to welcome them to the neighbourhood. Write a letter to your new neighbours. " +
      TASK1_GENERAL_INSTRUCTION_PREFIX,
    bulletPoints: [
      "introduce yourself",
      "tell them about some useful local services and shops",
      "invite them to visit your home",
    ],
  },
  {
    id: "t1-g-refund",
    module: "general",
    taskType: "task1",
    promptText:
      "You recently bought a piece of equipment online, but it did not work properly. Write a letter to the company. " +
      TASK1_GENERAL_INSTRUCTION_PREFIX,
    bulletPoints: [
      "describe what you bought and when",
      "explain what is wrong with it",
      "say what you would like the company to do",
    ],
  },
  // ---- Task 2 — Academic essay statements ----
  {
    id: "t2-a-technology",
    module: "academic",
    taskType: "task2",
    promptText:
      "Some people believe that the increasing use of computers and smartphones for communication has had a negative effect on young people's reading and writing skills. To what extent do you agree or disagree? Give reasons for your answer and include relevant examples. Write at least 250 words.",
  },
  {
    id: "t2-a-environment",
    module: "academic",
    taskType: "task2",
    promptText:
      "Many governments think economic progress is their most important goal. Some people, however, think that other types of progress are equally important for a country. Discuss both these views and give your own opinion. Write at least 250 words.",
  },
  // ---- Task 2 — General Training essay statements ----
  {
    id: "t2-g-volunteering",
    module: "general",
    taskType: "task2",
    promptText:
      "Some people think that young people should be required to do unpaid community work in their free time. Do you agree or disagree? Give reasons for your answer and include relevant examples from your own knowledge or experience. Write at least 250 words.",
  },
  {
    id: "t2-g-shopping",
    module: "general",
    taskType: "task2",
    promptText:
      "Nowadays many people prefer to shop online rather than in physical stores. What are the advantages and disadvantages of this trend? Give reasons for your answer and include relevant examples. Write at least 250 words.",
  },
];

export const stimuliFor = (module: IeltsModule, taskType: IeltsTaskType): Stimulus[] =>
  STIMULI.filter((s) => s.module === module && s.taskType === taskType);

export const getStimulus = (
  module: IeltsModule,
  taskType: IeltsTaskType,
  id: string,
): Stimulus | undefined => stimuliFor(module, taskType).find((s) => s.id === id);

// Render the marking ground-truth as text for the marker prompt. For charts this
// is the underlying data table (so the model can check the writer's figures and
// trends); for letters the required bullet points; for essays nothing extra (the
// statement is already in promptText).
export const serialiseStimulusForMarking = (s: Stimulus): string => {
  if (s.chartSpec) {
    const cs = s.chartSpec;
    const lines = cs.series.map(
      (series) =>
        `  ${series.name}: ${series.points.map((p) => `${p.label}=${p.value}${cs.unit}`).join(", ")}`,
    );
    return [
      `CHART (${cs.kind}) — "${cs.title}"`,
      `Category axis: ${cs.categoryAxis}; unit: ${cs.unit}.`,
      `Underlying data (GROUND TRUTH for checking accuracy). The learner sees these values plotted against a numbered axis, so they CAN read and cite approximate figures. Credit reasonable approximations (e.g. "about 14 cups") and judge accuracy against these numbers; do NOT demand exact values:`,
      ...lines,
    ].join("\n");
  }
  if (s.bulletPoints) {
    return `The letter MUST cover all three required points:\n${s.bulletPoints.map((b) => `  - ${b}`).join("\n")}`;
  }
  return "";
};
